import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
	createProject,
	getProjectMembers,
	getToolPolicyDocument,
	listUsers,
	replaceProjectMembers,
	replaceToolPolicyDocument,
	ToolPolicyConflictError,
	updateProject,
	type Project,
} from "@/lib/api";
import { TextInputField } from "@/components/common/TextInputField";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { Icon } from "@/components/common/Icon";
import { RichTooltip } from "@/components/common/RichTooltip";
import { UserList } from "@/components/common/UserList";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { focusRingOff, focusRingOn, INPUT_STYLE } from "@/lib/form-styles";
import { formatFullName } from "@/lib/format-name";
import { parseToolPolicyDocument, serializeToolPolicyDocument } from "@/lib/tool-policy-document";
import type { UserListUser } from "@/lib/user-list";

const ERROR_RING = "0 0 0 1px var(--danger-solid)";

const MEMBER_NOTES = [
	"リストにいるユーザーだけがこのプロジェクト (チャット・エージェントを含む) を閲覧できます。",
	"管理者はリストに関わらず全てのプロジェクトを閲覧できます。",
];

const POLICY_NOTES = [
	"deny(拒否) /ask(確認) / allow(許可)の配列にルールを並べます。",
	"ツールは「ツール名(引数パターン)」、対象ファイルは「Edit(グロブ)」で指定します (*は全ツール、Read(グロブ)はdeny専用)。",
	"複数のルールに一致したら最も制限の強いものを適用します。",
	"このプロジェクトのチャットが起点のジョブに適用されます。",
];

/** 並び順を無視して同じユーザー ID 集合かを返す。 */
function sameIdSet(a: string[], b: string[]): boolean {
	if (a.length !== b.length) return false;
	const set = new Set(b);
	return a.every((id) => set.has(id));
}

interface ProjectDialogProps {
	open: boolean;
	/** null なら新規作成、指定するとそのプロジェクトの編集 (名前とツール実行ポリシー) になる。 */
	project: Project | null;
	onClose: () => void;
	onSaved: (project: Project) => void;
}

/**
 * プロジェクトの作成・編集を行うモーダルダイアログ。
 * 編集時はツール実行ポリシーも一緒に編集でき、「保存」で名前とまとめて保存する
 * (新規作成時のポリシーはサーバーが既定値をシードする)。
 */
export function ProjectDialog({ open, project, onClose, onSaved }: ProjectDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const isEdit = project !== null;
	const organizationId = useCurrentUserStore((s) => s.organizationId);
	// ダイアログ自体がプロジェクト管理の権限を持つユーザーにしか開けないため、編集時は常にメンバーを編集できる。
	const canEditMembers = isEdit;

	const [name, setName] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const [memberIds, setMemberIds] = useState<string[]>([]);
	const [savedMemberIds, setSavedMemberIds] = useState<string[]>([]);
	const [memberUsers, setMemberUsers] = useState<UserListUser[]>([]);
	const [membersLoading, setMembersLoading] = useState(false);
	const [memberError, setMemberError] = useState<string | null>(null);
	const [membersLoadError, setMembersLoadError] = useState<string | null>(null);

	const [policyText, setPolicyText] = useState("");
	const [policySavedText, setPolicySavedText] = useState("");
	const [policyRevision, setPolicyRevision] = useState(0);
	const [policyError, setPolicyError] = useState<string | null>(null);
	const [policyLoading, setPolicyLoading] = useState(false);
	const [policyLoadError, setPolicyLoadError] = useState<string | null>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	// paint 前に前回の内容をリセットし、再オープン時に一瞬前回の値が見えるのを防ぐ。
	useLayoutEffect(() => {
		if (open) {
			setName(project?.name ?? "");
			setError(null);
			setSubmitError(null);
			setLoading(false);
			setMemberIds([]);
			setSavedMemberIds([]);
			setMemberError(null);
			setMembersLoadError(null);
			setMembersLoading(canEditMembers);
			setPolicyText("");
			setPolicySavedText("");
			setPolicyError(null);
			setPolicyLoadError(null);
			setPolicyLoading(project !== null);
		}
	}, [open, project, canEditMembers]);

	// 編集時、管理者はメンバーリストと追加候補 (組織のユーザー) を読み込む。
	// 取得に失敗しても名前やポリシーの保存は妨げない。
	useEffect(() => {
		if (!open || project === null || !canEditMembers) return;
		let cancelled = false;
		Promise.all([getProjectMembers(project.id), listUsers()])
			.then(([userIds, users]) => {
				if (cancelled) return;
				setMemberIds(userIds);
				setSavedMemberIds(userIds);
				// SuperAdmin の listUsers は全組織を返すため、このプロジェクトの組織に絞る。
				setMemberUsers(
					users
						.filter((u) => u.organizationId === organizationId)
						.map((u) => ({ id: u.id, name: formatFullName(u), email: u.email }))
						.sort((a, b) => a.name.localeCompare(b.name, "ja")),
				);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setMembersLoadError(
						err instanceof Error ? err.message : "メンバーを取得できませんでした。",
					);
				}
			})
			.finally(() => {
				if (!cancelled) setMembersLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, project, canEditMembers, organizationId]);

	// 編集時はポリシーを読み込む。取得に失敗しても名前だけの保存は妨げない。
	useEffect(() => {
		if (!open || project === null) return;
		let cancelled = false;
		getToolPolicyDocument(project.id)
			.then(({ tools, files, revision }) => {
				if (cancelled) return;
				const serialized = serializeToolPolicyDocument(tools, files);
				setPolicyText(serialized);
				setPolicySavedText(serialized);
				setPolicyRevision(revision);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setPolicyLoadError(
						err instanceof Error ? err.message : "ツール実行ポリシーを取得できませんでした。",
					);
				}
			})
			.finally(() => {
				if (!cancelled) setPolicyLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, project]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				e.preventDefault();
			}
		},
		[onClose],
	);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!name.trim()) {
			setError("プロジェクト名は必須です。");
			return;
		}
		// ポリシーは変更があるときだけ検証・保存する。構文エラーなら名前も保存せずに止める
		// (「保存」1 つでまとめて確定させ、片方だけ意図せず保存される事態を減らす)。
		const policyDirty = isEdit && policyLoadError === null && policyText !== policySavedText;
		let parsedPolicy: ReturnType<typeof parseToolPolicyDocument> | null = null;
		if (policyDirty) {
			parsedPolicy = parseToolPolicyDocument(policyText);
			if ("error" in parsedPolicy) {
				setPolicyError(parsedPolicy.error);
				return;
			}
		}
		setError(null);
		setSubmitError(null);
		setPolicyError(null);
		setMemberError(null);
		setLoading(true);
		try {
			// メンバーは変更があるときだけ保存する (ポリシーと同じく「保存」1 つでまとめて確定)。
			const membersDirty =
				canEditMembers && membersLoadError === null && !sameIdSet(memberIds, savedMemberIds);
			if (membersDirty) {
				try {
					const saved = await replaceProjectMembers(project!.id, memberIds);
					setMemberIds(saved);
					setSavedMemberIds(saved);
				} catch (caught: unknown) {
					setMemberError(
						caught instanceof Error ? caught.message : "メンバーを保存できませんでした。",
					);
					return;
				}
			}
			if (parsedPolicy !== null && "tools" in parsedPolicy) {
				try {
					const nextRevision = await replaceToolPolicyDocument(project!.id, {
						...parsedPolicy,
						revision: policyRevision,
					});
					setPolicySavedText(policyText);
					setPolicyRevision(nextRevision);
				} catch (caught: unknown) {
					if (caught instanceof ToolPolicyConflictError && caught.revision !== null) {
						// 別画面の保存と競合。編集内容は残したまま基準 revision だけ最新化し、
						// メッセージを確認したうえでの再保存 (明示的な上書き) を可能にする。
						setPolicyRevision(caught.revision);
					}
					setPolicyError(
						caught instanceof Error ? caught.message : "ポリシーを保存できませんでした。",
					);
					return;
				}
			}
			const saved = isEdit
				? await updateProject(project.id, name.trim())
				: await createProject(name.trim());
			onSaved(saved);
			onClose();
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			data-testid="project-dialog"
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width: isEdit ? 560 : 480,
			}}
			className="backdrop:bg-scrim"
		>
			<form onSubmit={handleSubmit} noValidate>
				<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
					<h3
						style={{
							margin: 0,
							fontSize: "var(--text-title)",
							fontWeight: 600,
							color: "var(--fg-1)",
							fontFamily: "var(--font-sans)",
						}}
					>
						{isEdit ? "プロジェクトを編集" : "プロジェクトを作成"}
					</h3>
				</div>
				<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
					<TextInputField
						label="プロジェクト名"
						value={name}
						onChange={setName}
						autoComplete="off"
						error={error ?? undefined}
					/>
					{canEditMembers && (
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{
									display: "flex",
									alignItems: "center",
									gap: 6,
									fontSize: "var(--text-caption)",
									fontWeight: 500,
									color: "var(--fg-2)",
									fontFamily: "var(--font-sans)",
								}}
							>
								メンバー
								<RichTooltip
									maxWidth={420}
									// 説明を求めて info アイコンへ寄せる操作なので、待たせずに出す。
									delayMs={0}
									content={
										<ul style={{ margin: 0, paddingLeft: 16, listStyle: "disc" }}>
											{MEMBER_NOTES.map((note) => (
												<li key={note}>{note}</li>
											))}
										</ul>
									}
								>
									<span
										data-testid="project-members-info"
										style={{ display: "inline-flex", color: "var(--fg-2)", cursor: "help" }}
									>
										<Icon name="info" size={14} />
									</span>
								</RichTooltip>
							</span>
							{membersLoadError !== null && (
								<p style={{ margin: 0, color: "var(--danger)", fontSize: "var(--text-caption)" }}>
									{membersLoadError}
								</p>
							)}
							{membersLoadError === null && (
								<>
									{membersLoading ? (
										<p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--text-caption)" }}>
											読み込み中…
										</p>
									) : (
										<UserList users={memberUsers} selectedIds={memberIds} onChange={setMemberIds} />
									)}
									{memberError !== null && (
										<p
											className="select-text"
											style={{
												margin: 0,
												fontSize: "var(--text-caption)",
												color: "var(--danger)",
												fontFamily: "var(--font-sans)",
											}}
										>
											{memberError}
										</p>
									)}
								</>
							)}
						</div>
					)}
					{isEdit && (
						<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{
									display: "flex",
									alignItems: "center",
									gap: 6,
									fontSize: "var(--text-caption)",
									fontWeight: 500,
									color: "var(--fg-2)",
									fontFamily: "var(--font-sans)",
								}}
							>
								ツール実行ポリシー
								<RichTooltip
									maxWidth={420}
									// 説明を求めて info アイコンへ寄せる操作なので、待たせずに出す。
									delayMs={0}
									content={
										<ul style={{ margin: 0, paddingLeft: 16, listStyle: "disc" }}>
											{POLICY_NOTES.map((note) => (
												<li key={note}>{note}</li>
											))}
										</ul>
									}
								>
									<span
										data-testid="tool-policy-notes-info"
										style={{ display: "inline-flex", color: "var(--fg-2)", cursor: "help" }}
									>
										<Icon name="info" size={14} />
									</span>
								</RichTooltip>
							</span>
							{policyLoadError !== null && (
								<p
									style={{
										margin: 0,
										color: "var(--danger)",
										fontSize: "var(--text-caption)",
									}}
								>
									{policyLoadError}
								</p>
							)}
							{policyLoadError === null && (
								<>
									{/* 読み込み中も textarea を描画して高さを一定に保ち、
									    完了時にダイアログが再センタリングでずれるのを防ぐ。 */}
									<textarea
										value={policyText}
										onChange={(e) => setPolicyText(e.target.value)}
										spellCheck={false}
										disabled={policyLoading}
										placeholder={policyLoading ? "読み込み中…" : undefined}
										data-testid="tool-policy-document-textarea"
										style={{
											...INPUT_STYLE,
											display: "block",
											height: 260,
											padding: "8px 10px",
											fontFamily: "var(--font-mono)",
											fontSize: "var(--text-caption)",
											lineHeight: "var(--leading-body)",
											tabSize: 2,
											resize: "vertical",
											...(policyError !== null ? { boxShadow: ERROR_RING } : {}),
										}}
										onFocus={(e) => focusRingOn(e.currentTarget)}
										onBlur={(e) => {
											// 通常のリング復帰では構文エラーの赤リングが消えてしまうため、
											// エラー中はエラーリングへ戻す。
											if (policyError !== null) {
												e.currentTarget.style.boxShadow = ERROR_RING;
											} else {
												focusRingOff(e.currentTarget);
											}
										}}
									/>
									{policyError !== null && (
										<p
											className="select-text"
											style={{
												margin: 0,
												fontSize: "var(--text-caption)",
												color: "var(--danger)",
												fontFamily: "var(--font-sans)",
											}}
										>
											{policyError}
										</p>
									)}
								</>
							)}
						</div>
					)}
					{submitError && (
						<div
							style={{
								padding: "10px 14px",
								borderRadius: "var(--radius-3)",
								background: "var(--danger-subtle)",
								boxShadow: "0 0 0 1px var(--danger-line)",
								fontSize: "var(--text-ui)",
								color: "var(--danger)",
								fontFamily: "var(--font-sans)",
							}}
						>
							{submitError}
						</div>
					)}
				</div>
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						borderTop: "var(--pane-header-border)",
						padding: "12px 24px",
					}}
				>
					<ConfirmButton
						variant="ghost"
						label="キャンセル"
						loading={false}
						disabled={loading}
						onClick={onClose}
					/>
					<ConfirmButton
						type="submit"
						label={isEdit ? "保存" : "作成"}
						loading={loading}
						disabled={!name.trim() || policyLoading || membersLoading}
						data-testid="project-dialog-submit"
					/>
				</div>
			</form>
		</dialog>
	);
}
